import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLang } from "../context/LanguageContext";
import api, { formatApiError, API_BASE } from "../lib/api";
import useMicRecorder from "../lib/useMicRecorder";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Bot, Send, X, Sparkles, Maximize2, Volume2, Square, Mic } from "lucide-react";
import { toast } from "sonner";

function splitCitations(text) {
  if (!text) return { body: "", sources: [] };
  const re = /(?:^|\n)\s*(?:Sumber|Source)\s*:\s*([^\n]+)\s*$/i;
  const m = text.match(re);
  if (!m) return { body: text.trim(), sources: [] };
  const raw = m[1];
  const sources = raw.split(/[,;/]/).map((s) => s.trim()).filter(Boolean);
  return { body: text.slice(0, m.index).trim(), sources };
}

export default function FloatingGracely() {
  const { t, lang } = useLang();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [sessionId] = useState("main");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [unseen, setUnseen] = useState(true);
  const [playingId, setPlayingId] = useState(null);
  const audioRef = useRef(null);
  const bottomRef = useRef(null);
  const loadedRef = useRef(false);
  const sendRef = useRef(null);
  const { recording, transcribing, start, stop } = useMicRecorder({
    language: lang,
    onTranscript: ({ text, audio_b64, audio_mime }) => {
      setTimeout(() => sendRef.current?.(text, { audio_b64, audio_mime, voice_reply: true }), 60);
    },
  });

  const greet = lang === "id"
    ? "Halo, saya Gracely. Ada yang bisa saya bantu hari ini?"
    : "Hi, I'm Gracely. How can I support you today?";

  const loadHistory = async () => {
    try {
      const { data } = await api.get("/chat/history", { params: { session_id: sessionId } });
      if (data.messages && data.messages.length > 0) {
        setMessages(data.messages.map((m) => ({ id: m.id, role: m.role, content: m.content, has_audio: m.has_audio })));
      } else {
        setMessages([{ role: "assistant", content: greet }]);
      }
    } catch {
      setMessages([{ role: "assistant", content: greet }]);
    }
  };

  useEffect(() => {
    if (open && !loadedRef.current) {
      loadedRef.current = true;
      loadHistory();
    }
    if (open) {
      setUnseen(false);
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 60);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, messages.length]);

  useEffect(() => {
    loadedRef.current = false;
  }, [lang]);

  const suggestions = lang === "id"
    ? ["Rekomendasi olahraga hari ini?", "Apa menu aman untuk sarapan?", "Bahaya HFCS untukku?"]
    : ["What workout should I do today?", "What's a safe breakfast?", "Is HFCS bad for me?"];

  const send = async (msgText, extras = {}) => {
    const msg = (msgText ?? input).trim();
    if (!msg || sending) return;
    setMessages((prev) => [...prev, { role: "user", content: msg, has_audio: !!extras.audio_b64 }]);
    setInput("");
    setSending(true);
    try {
      const body = { session_id: sessionId, message: msg, lang };
      if (extras.audio_b64) {
        body.user_audio_b64 = extras.audio_b64;
        body.user_audio_mime = extras.audio_mime;
        body.voice_reply = !!extras.voice_reply;
      }
      const { data } = await api.post("/chat/message", body);
      setMessages((prev) => {
        const next = [...prev];
        for (let i = next.length - 1; i >= 0; i--) {
          if (next[i].role === "user" && !next[i].id) {
            next[i] = { ...next[i], id: data.user_message_id, has_audio: data.has_user_audio };
            break;
          }
        }
        next.push({ id: data.assistant_message_id, role: "assistant", content: data.reply, has_audio: data.has_assistant_audio });
        return next;
      });
      if (extras.voice_reply && data.has_assistant_audio) {
        setTimeout(() => playMessageAudio(data.assistant_message_id), 200);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSending(false);
    }
  };
  sendRef.current = send;

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setPlayingId(null);
  };

  const playMessageAudio = async (id) => {
    if (playingId === id) return stopAudio();
    stopAudio();
    setPlayingId(id);
    try {
      const token = localStorage.getItem("gracely_token");
      const res = await fetch(`${API_BASE}/chat/audio/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("no audio");
      const blob = await res.blob();
      const audio = new Audio(URL.createObjectURL(blob));
      audioRef.current = audio;
      audio.onended = () => setPlayingId(null);
      audio.play();
    } catch {
      setPlayingId(null);
    }
  };

  const speakText = async (id, text) => {
    if (playingId === id) return stopAudio();
    stopAudio();
    setPlayingId(id);
    try {
      const token = localStorage.getItem("gracely_token");
      const res = await fetch(`${API_BASE}/tts`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ text, voice: "nova" }),
      });
      if (!res.ok) throw new Error("TTS failed");
      const blob = await res.blob();
      const audio = new Audio(URL.createObjectURL(blob));
      audioRef.current = audio;
      audio.onended = () => setPlayingId(null);
      audio.play();
    } catch {
      setPlayingId(null);
    }
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-50 group"
          aria-label="Open Gracely chat"
          data-testid="floating-gracely-btn"
        >
          <span className="relative float-bob inline-flex">
            <span className="pulse-ring" />
            <span className="relative w-16 h-16 rounded-full bg-gradient-to-br from-[#4A5D23] to-[#3A4A1C] shadow-[0_18px_40px_-12px_rgba(74,93,35,0.6)] flex items-center justify-center text-white group-hover:scale-105 transition-transform">
              <Bot className="w-7 h-7" strokeWidth={2.2} />
              <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-[#E07A5F] border-2 border-white flex items-center justify-center">
                <Sparkles className="w-2 h-2 text-white" />
              </span>
              {unseen && (
                <span className="absolute -bottom-9 left-1/2 -translate-x-1/2 whitespace-nowrap text-[11px] font-semibold text-stone-700 bg-white px-3 py-1 rounded-full shadow border border-stone-200">
                  {lang === "id" ? "Sapa Gracely" : "Say hi to Gracely"}
                </span>
              )}
            </span>
          </span>
        </button>
      )}

      {open && (
        <div className="fixed bottom-4 right-4 z-50 w-[min(420px,calc(100vw-2rem))] h-[min(620px,calc(100vh-2rem))] card-soft flex flex-col overflow-hidden fade-in" data-testid="floating-gracely-panel">
          <div className="p-4 flex items-center justify-between bg-gradient-to-br from-[#4A5D23] to-[#3A4A1C] text-white">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center float-bob">
                <Bot className="w-5 h-5" strokeWidth={2.2} />
              </div>
              <div>
                <p className="font-serif-display text-xl leading-none">Gracely</p>
                <p className="text-[10px] uppercase tracking-widest text-white/70 mt-1">{lang === "id" ? "Online · Siap membantu" : "Online · Here for you"}</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => { setOpen(false); nav("/chat"); }} className="p-2 rounded-full hover:bg-white/10 transition-colors" aria-label="Expand" data-testid="expand-gracely-btn">
                <Maximize2 className="w-4 h-4" />
              </button>
              <button onClick={() => setOpen(false)} className="p-2 rounded-full hover:bg-white/10 transition-colors" aria-label="Close" data-testid="close-gracely-btn">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#FAF9F6]">
            {messages.map((m, i) => {
              const { body, sources } = m.role === "assistant" ? splitCitations(m.content) : { body: m.content, sources: [] };
              const key = m.id || `local-${i}`;
              const canPlaySaved = m.id && m.has_audio;
              const isPlaying = playingId === m.id;
              return (
                <div key={key} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-[14px] leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-stone-900 text-white rounded-br-md" : "bg-white border border-stone-200 text-stone-900 rounded-bl-md"}`}>
                    <div className="flex items-center justify-between gap-2 mb-1">
                      {m.role === "user" && m.has_audio && (
                        <div className="flex items-center gap-1 text-[9px] font-bold uppercase tracking-widest opacity-70">
                          <Mic className="w-2.5 h-2.5" /> {lang === "id" ? "Suaramu" : "Your voice"}
                        </div>
                      )}
                      {(canPlaySaved || m.role === "assistant") && (
                        <button
                          onClick={() => canPlaySaved ? playMessageAudio(m.id) : speakText(key, body)}
                          className={`ml-auto ${m.role === "user" ? "text-white/80" : "text-[#4A5D23]"} hover:opacity-70`}
                          data-testid={`floating-play-${i}`}
                        >
                          {isPlaying ? <Square className="w-3.5 h-3.5 fill-current" /> : <Volume2 className="w-3.5 h-3.5" />}
                        </button>
                      )}
                    </div>
                    <div>{body}</div>
                    {sources.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {sources.map((s, si) => (
                          <span key={si} className="text-[9px] font-bold uppercase tracking-wide bg-[#EAF0E4] text-[#4A5D23] px-1.5 py-0.5 rounded-full">
                            {s}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            {sending && (
              <div className="flex justify-start">
                <div className="bg-white border border-stone-200 px-3.5 py-2.5 rounded-2xl rounded-bl-md text-sm text-stone-500 flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#4A5D23] animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="w-1.5 h-1.5 rounded-full bg-[#4A5D23] animate-bounce" style={{ animationDelay: "120ms" }} />
                  <span className="w-1.5 h-1.5 rounded-full bg-[#4A5D23] animate-bounce" style={{ animationDelay: "240ms" }} />
                </div>
              </div>
            )}
            {messages.length <= 1 && !sending && (
              <div className="pt-2 flex flex-wrap gap-2">
                {suggestions.map((s, i) => (
                  <button key={i} onClick={() => send(s)} className="text-xs px-3 py-1.5 rounded-full bg-white border border-stone-200 text-stone-700 hover:border-[#4A5D23] hover:text-[#4A5D23] transition-colors" data-testid={`gracely-suggestion-${i}`}>
                    {s}
                  </button>
                ))}
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          <div className="p-3 border-t border-stone-200 bg-white flex gap-2 items-end">
            <button
              onClick={() => (recording ? stop() : start())}
              disabled={transcribing}
              className={`w-10 h-10 rounded-full flex items-center justify-center btn-pop flex-shrink-0 ${recording ? "bg-[#E07A5F] text-white animate-pulse" : "bg-stone-100 text-stone-700 hover:bg-stone-200"}`}
              aria-label="Mic"
              data-testid="floating-mic-btn"
            >
              <Mic className="w-4 h-4" />
            </button>
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              rows={1}
              placeholder={transcribing ? (lang === "id" ? "Menerjemahkan suara…" : "Transcribing…") : t("chat_placeholder")}
              className="border-transparent bg-stone-50 rounded-xl focus-visible:ring-1 focus-visible:ring-[#4A5D23]/40 resize-none min-h-[40px] text-sm"
              data-testid="floating-gracely-input"
            />
            <Button onClick={() => send()} disabled={sending || !input.trim()} className="rounded-full bg-[#4A5D23] hover:bg-[#3A4A1C] text-white btn-pop px-3 h-10" data-testid="floating-gracely-send">
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
